/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { CombinedFileScope } from './CombinedFileScope';
import { MIME_TYPE_PDF, specialDocumentTypes } from '../../../services/constants';
import { useAttachmentsValidations } from '../../../hooks/useAttachmentsValidations';
import { isAttachmentTypeUploaded, isAttachmentInputHidden } from '../../../services/attachments.helper';

const useStyles = makeStyles(theme => ({
  titleText: {
    color: theme.palette.titleText,
  },
  errorAlert: {
    border: `solid 1px ${theme.palette.error.main}`,
  },
}));

export function CombinedFile({ attachmentsMetadata, form, parentClasses, technicalAttachmentTypes, readOnly }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { gapsValidation, overlapValidation, attachmentsConfigReady } = useAttachmentsValidations();

  const getTypes = useMemo(() => {
    return technicalAttachmentTypes
      ?.filter(attachment => attachment.type !== specialDocumentTypes.COMBINED && !attachment.otherLanguage)
      .filter(attachment => attachment.acceptedMimeTypes?.indexOf(MIME_TYPE_PDF) !== -1)
      .map(type => type.type);
  }, [technicalAttachmentTypes]);

  const getValues = useMemo(() => {
    return technicalAttachmentTypes
      ?.map(type => [type.type])
      .reduce((acc, type) => [...acc, `${type}from`, `${type}to`], []);
  }, [technicalAttachmentTypes]);

  const gapsValidate = gapsValidation({ form });
  const overlapsValidate = overlapValidation({ form });
  const { trigger, errors } = form;

  useEffect(() => {
    (async () => {
      await trigger(['gap', 'overlap']);
    })();
  }, [trigger, ...Object.values(getValues)]);

  return (
    attachmentsConfigReady && (
      <Box>
        <Typography variant="subtitle1" component="span" gutterBottom className={classes.titleText}>
          {t('attachments.select.types')}
        </Typography>
        {getTypes.map((type) => {
          return (
            <CombinedFileScope
              key={type}
              type={type}
              form={form}
              parentClasses={parentClasses}
              hiddenInputs={isAttachmentInputHidden(technicalAttachmentTypes, type)}
              disabled={isAttachmentTypeUploaded(attachmentsMetadata, type)}
              readOnly={readOnly}
            />
          );
        })}
        <input type="hidden" required={true} name="overlap" ref={form.register(overlapsValidate)} />
        <input type="hidden" required={true} name="gap" ref={form.register(gapsValidate)} />
        {errors?.overlap && (
          <Alert data-testid="overlap-error" severity="error" className={classes.errorAlert}>
            {t(errors.overlap?.message)}
          </Alert>
        )}
        {errors?.gap && (
          <Alert data-testid="gap-error" severity="error" className={classes.errorAlert}>
            {t(errors.gap?.message)}
          </Alert>
        )}
      </Box>
    )
  );
}
